import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { MAX_RUNS, failedSlot, runCharts } from '../services/charts';

/** A visible dashboard re-runs this often (plan §4.5 "Live"). */
export const REFRESH_MS = 5 * 60 * 1000;
/** After this long without a pointer, a key, a scroll or a touch, it stops re-running (plan §5.5). */
export const IDLE_MS = 30 * 60 * 1000;
/** A tab that comes back re-runs at once only when its numbers are at least this old. */
export const STALE_ON_RETURN_MS = 60 * 1000;
/** The longest wait a refused request (429, 503) is waited out by itself, once. */
export const MAX_RETRY_WAIT_S = 60;
/** What a person does that counts as being there. */
export const ACTIVITY_EVENTS = Object.freeze(['pointerdown', 'keydown', 'scroll', 'touchstart', 'wheel']);

// Keyed by tile id in a Map: a tile id is any 1–32 of A-Za-z0-9_-, `__proto__` included.
const NO_RESULTS = new Map();

/** What a tile with no answer yet says when the page's filter was refused. */
export const NOT_DRAWN = "Not drawn: the filter can't be read.";
const notDrawn = (chartId) => ({ ok: false, chart_id: chartId || null, error: { code: 'not_drawn', message: NOT_DRAWN, control: 'filter' } });

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/** The chart tiles a dashboard runs: the available ones, in order, at most MAX_RUNS. */
export const runnableTiles = (tiles) => (Array.isArray(tiles) ? tiles : [])
	.filter((tile) => tile && tile.kind === 'chart' && tile.available === true && tile.chart_id)
	.slice(0, MAX_RUNS);

/**
 * A dashboard's numbers (charts A6, plan §4.5, §5.5): ONE batch request
 * (`POST /api/charts/run`) for every available chart tile — text tiles and
 * unavailable charts are not run — each run `{ chart_id, collection_id: the
 * page's collection (the context), range?, filter? }`, the answers matched
 * back to their tiles by place.
 *
 *   tiles         the dashboard's tiles (the draft's, while arranging)
 *   collectionId  the CONTEXT whose items every chart counts
 *   range         undefined — each chart keeps its own; otherwise sent,
 *                 replacing each dated chart's range (`null` is all time)
 *   filter        the dashboard's filter, ANDed on each chart by the server
 *   enabled       false asks nothing
 *
 * LIVE. While the tab is visible it re-runs every REFRESH_MS, and when the
 * tab comes back it re-runs at once if its numbers are older than
 * STALE_ON_RETURN_MS. After IDLE_MS with no pointer, key, scroll or touch it
 * stops (`paused`: "Paused. Refresh to update."). `refresh()` always runs
 * and un-pauses; so does a change of what is asked (a control, a tile added).
 *
 * ONE REQUEST AT A TIME. The server holds one chart request per person in
 * flight and refuses a second (429 `budget: 'inFlight'`), and aborting does
 * not free its slot, so a run never starts while one is out: a change waits
 * and is asked when the answer lands; a timer or a returning tab finds the
 * one out fresh enough. A refused request (429, 503) that names a wait of at
 * most MAX_RETRY_WAIT_S is waited out and asked again ONCE; otherwise — or
 * when the retry fails too — each tile gets the failed slot, so each chart
 * says why in its own frame (failure is per tile, never per page).
 *
 * While a re-run is out every chart keeps its last answer (`loading`: the
 * frame holds it dimmed — no skeleton, no layout jump). An answer to what is
 * no longer asked (the range or filter changed meanwhile) is dropped.
 *
 * A FILTER THE SERVER CANNOT READ (the batch's 400 `invalid_filter`, a
 * hand-edited `?q=` that does not parse) is the page's, not each chart's
 * (ruled by the coordinator): `filterError` carries the server's sentence,
 * every tile keeps its last answer — one with none says "Not drawn: the
 * filter can't be read." (NOT_DRAWN), never a spinner — and nothing re-runs
 * on the clock or on a returning tab while that same question is asked
 * (`refused`); Refresh still asks. The next answer clears it.
 *
 * A RETRY (a 429 or 503 waited out) is the clock's run, not the person's: it
 * never goes out in a hidden tab or wakes a paused one, and every way it can
 * end — nothing left to run, the hook switched off, the tiles changed during
 * its wait — gives the one request's place back (the second review's F5:
 * it stayed "in flight" forever). A retry cancelled because the tiles or the
 * switch changed is owed: what is asked now goes out at once (or when the
 * hook is switched back on), so the clock never stops with it (N1). Tiles
 * only moved are not a change; the retry goes out when it was due. The owed
 * run is still the clock's (the fourth review's L3): unless a tile has no
 * answer or the question changed — the person's asks — it never goes out
 * from a hidden tab or wakes a paused page; a tab that comes back asks if
 * its numbers are old, and re-arms the clock if not.
 *
 * Returns { results: Map<tileId, slot>, loading, ranAt, paused, filterError, refresh }.
 */
export default function useDashboardRun({ tiles, collectionId, range, filter = '', enabled = true }) {
	const runTiles = useMemo(() => runnableTiles(tiles), [tiles]);
	const filterText = typeof filter === 'string' ? filter.trim() : '';
	// What is asked, beside which tiles: an answer is for this, or it is old.
	const paramsKey = JSON.stringify([collectionId || null, range === undefined ? 'own' : range, filterText]);

	const [results, setResults] = useState(NO_RESULTS);
	const [ranAt, setRanAt] = useState(null);
	const [loading, setLoading] = useState(false);
	const [paused, setPaused] = useState(false);
	const [filterError, setFilterError] = useState(null);

	// Tiles that have no answer for what is asked now: their arrival asks again.
	const missingKey = runTiles.filter((tile) => !results.has(tile.id)).map((tile) => tile.id).sort().join(',');

	const latest = useRef({});
	latest.current = { runTiles, collectionId, range, filterText, paramsKey, enabled: Boolean(enabled) };
	const seq = useRef(0);
	const flying = useRef(false);
	const queued = useRef(false);
	const answered = useRef(null);
	// The question the server refused to read (a 400 invalid_filter): not
	// asked again by the clock or a returning tab while it is still asked.
	const refused = useRef(null);
	const lastRanMs = useRef(0);
	const lastActivity = useRef(Date.now());
	const pausedRef = useRef(false);
	const refreshTimer = useRef(null);
	const idleTimer = useRef(null);
	const retryTimer = useRef(null);
	const alive = useRef(true);
	const runRef = useRef(null);

	const clearTimer = (ref) => {
		if (ref.current) {
			clearTimeout(ref.current);
			ref.current = null;
		}
	};

	// The next scheduled re-run: REFRESH_MS after the last answer, only while
	// visible and not paused.
	const scheduleRefresh = useCallback(() => {
		clearTimer(refreshTimer);
		if (!alive.current || pausedRef.current || !isVisible()) return;
		const wait = Math.max(0, REFRESH_MS - (Date.now() - lastRanMs.current));
		refreshTimer.current = setTimeout(() => {
			refreshTimer.current = null;
			if (runRef.current) runRef.current({ manual: false });
		}, wait);
	}, []);

	const pause = useCallback(() => {
		pausedRef.current = true;
		setPaused(true);
		clearTimer(refreshTimer);
		clearTimer(idleTimer);
	}, []);

	// The idle watch: fires when IDLE_MS have passed since the last activity
	// it knows of, and re-arms for the rest when there was activity since.
	const armIdle = useCallback(() => {
		clearTimer(idleTimer);
		if (!alive.current || pausedRef.current) return;
		const left = IDLE_MS - (Date.now() - lastActivity.current);
		if (left <= 0) {
			pause();
			return;
		}
		idleTimer.current = setTimeout(() => {
			idleTimer.current = null;
			armIdle();
		}, left);
	}, [pause]);

	const unpause = useCallback(() => {
		lastActivity.current = Date.now();
		if (pausedRef.current) {
			pausedRef.current = false;
			setPaused(false);
		}
		armIdle();
	}, [armIdle]);

	// A retry that does not go out gives the one request's place back.
	const releaseRetry = useCallback((retry) => {
		if (!retry) return;
		flying.current = false;
		setLoading(false);
	}, []);

	const run = useCallback(async ({ manual = false, retry = false } = {}) => {
		const now = latest.current;
		if (!alive.current || !now.enabled || !now.collectionId) { releaseRetry(retry); return; }
		if (manual) unpause();
		else if (pausedRef.current || !isVisible()) { releaseRetry(retry); return; }
		// A question the server could not read is asked again only by the person.
		if (!manual && refused.current === now.paramsKey) { releaseRetry(retry); return; }
		if (flying.current && !retry) {
			// One request at a time: asked again once the answer lands.
			if (manual) queued.current = true;
			return;
		}
		const sentTiles = now.runTiles;
		const sentParams = now.paramsKey;
		if (!sentTiles.length) {
			answered.current = sentParams;
			releaseRetry(retry);
			return;
		}
		clearTimer(refreshTimer);
		seq.current += 1;
		const mine = seq.current;
		flying.current = true;
		// This request carries what is asked now: nothing queued is still owed (L2).
		queued.current = false;
		setLoading(true);
		const runs = sentTiles.map((tile) => {
			const out = { chart_id: tile.chart_id, collection_id: now.collectionId };
			if (now.range !== undefined) out.range = now.range;
			if (now.filterText) out.filter = now.filterText;
			return out;
		});
		let waiting = false;
		let unreadable = false;
		try {
			const answer = await runCharts(runs);
			if (!alive.current || mine !== seq.current) return;
			if (latest.current.paramsKey === sentParams) {
				const slots = sentTiles.map((tile, i) => [tile.id, answer.results[i] || failedSlot(null, tile.chart_id)]);
				setResults((current) => new Map([...current, ...slots]));
				setRanAt(answer.ranAt || null);
				setFilterError(null);
				refused.current = null;
				answered.current = sentParams;
				lastRanMs.current = Date.now();
			}
		} catch (e) {
			if (!alive.current || mine !== seq.current) return;
			const status = e && e.response ? e.response.status : null;
			const body = e && e.response && e.response.data ? e.response.data : null;
			if (status === 400 && body && body.code === 'invalid_filter') {
				// The page says it once; the tiles keep what they last showed.
				unreadable = true;
				if (latest.current.paramsKey === sentParams) {
					setFilterError(typeof body.message === 'string' && body.message.trim() ? body.message.trim() : 'The server could not read it.');
					// A tile with no answer to keep says so, never spins (L4).
					setResults((current) => {
						const missing = sentTiles.filter((tile) => !current.has(tile.id));
						return missing.length ? new Map([...current, ...missing.map((tile) => [tile.id, notDrawn(tile.chart_id)])]) : current;
					});
					refused.current = sentParams;
					answered.current = sentParams;
					lastRanMs.current = Date.now();
				}
				return;
			}
			const slot = failedSlot(e);
			const named = slot.error && typeof slot.error.retryAfter === 'number' ? slot.error.retryAfter : null;
			if (!retry && (status === 429 || status === 503) && named !== null && named <= MAX_RETRY_WAIT_S) {
				// The server named its wait: waited out, and asked again once.
				waiting = true;
				retryTimer.current = setTimeout(() => {
					retryTimer.current = null;
					// The clock's run, not the person's: a hidden or paused page lets it go (L3).
					if (runRef.current) runRef.current({ manual: false, retry: true });
				}, Math.max(1, Math.ceil(named)) * 1000);
				return;
			}
			if (latest.current.paramsKey === sentParams) {
				const slots = sentTiles.map((tile) => [tile.id, failedSlot(e, tile.chart_id)]);
				setResults((current) => new Map([...current, ...slots]));
				answered.current = sentParams;
				lastRanMs.current = Date.now();
			}
		} finally {
			if (alive.current && mine === seq.current && !waiting) {
				flying.current = false;
				setLoading(false);
				if (queued.current || latest.current.paramsKey !== sentParams) {
					queued.current = false;
					setTimeout(() => { if (runRef.current) runRef.current({ manual: true }); }, 0);
				} else if (!unreadable) {
					scheduleRefresh();
				}
			}
		}
	}, [unpause, scheduleRefresh, releaseRetry]);
	runRef.current = run;

	// The tiles or the switch changing during a retry's wait cancels the retry
	// and gives its place back, and the run it owed is asked by the effect
	// below, at once, in the same breath as anything else it asks — one
	// request (the third review's N1: a cancel that left the asking to the
	// effect below asked nothing when no tile was new, and the clock stopped
	// for good). The key is the SET of chart tiles, not their order: a tile
	// moved is no change, and the retry goes out when it was due.
	const tilesKey = runTiles.map((tile) => `${tile.id}:${tile.chart_id}`).sort().join(',');
	const owed = useRef(false);
	useEffect(() => {
		if (!retryTimer.current) return;
		clearTimer(retryTimer);
		flying.current = false;
		queued.current = false;
		setLoading(false);
		owed.current = true;
	}, [tilesKey, enabled]);

	// Another context starts from nothing, and any answer still out is old.
	useEffect(() => {
		seq.current += 1;
		flying.current = false;
		queued.current = false;
		owed.current = false;
		answered.current = null;
		refused.current = null;
		clearTimer(retryTimer);
		setResults(NO_RESULTS);
		setRanAt(null);
		setLoading(false);
		setFilterError(null);
	}, [collectionId]);

	// What is asked changed, a tile has no answer yet, or a cancelled retry
	// owes its run: one batch for all. A change is the person's ask; the owed
	// run alone stays the clock's, so a hidden tab or a paused page lets it go
	// and the returning tab decides (L3).
	useEffect(() => {
		if (!enabled) return;
		const changed = answered.current !== paramsKey || Boolean(missingKey);
		if (!changed && !owed.current) return;
		owed.current = false;
		run({ manual: changed });
	}, [enabled, paramsKey, missingKey, tilesKey, run]);

	// The tab coming back, and the person being there.
	useEffect(() => {
		alive.current = true;
		lastActivity.current = Date.now();
		armIdle();
		const onVisibility = () => {
			if (!isVisible()) {
				clearTimer(refreshTimer);
				return;
			}
			if (pausedRef.current) return;
			if (Date.now() - lastActivity.current >= IDLE_MS) {
				pause();
				return;
			}
			if (Date.now() - lastRanMs.current >= STALE_ON_RETURN_MS) run({ manual: false });
			else scheduleRefresh();
		};
		const onActivity = () => { lastActivity.current = Date.now(); };
		document.addEventListener('visibilitychange', onVisibility);
		ACTIVITY_EVENTS.forEach((type) => window.addEventListener(type, onActivity, { capture: true, passive: true }));
		return () => {
			alive.current = false;
			seq.current += 1;
			clearTimer(refreshTimer);
			clearTimer(idleTimer);
			clearTimer(retryTimer);
			document.removeEventListener('visibilitychange', onVisibility);
			ACTIVITY_EVENTS.forEach((type) => window.removeEventListener(type, onActivity, { capture: true }));
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const refresh = useCallback(() => run({ manual: true }), [run]);

	return { results, loading, ranAt, paused, filterError, refresh };
}
